import { useCallback, useState } from 'react';
import axios from 'axios';
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { logout } from "utils";
import { toast } from "utils/utils";
import { ContentType } from "utils/constant";
import { storageKey, get } from "utils/storage";
import _ from 'lodash';

// ECONNABORTED 请求超时
const axiosInstance = axios.create({
  timeout: 10000,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/x-www-form-urlencoded",
  },
});

// 请求拦截
axiosInstance.interceptors.request.use(config => {
  // token handle
  const userInfo = get(storageKey.userInfo);
  config.headers.token = userInfo?.token || "";
  // method handle
  if (
    config.method.toLocaleLowerCase() === "post" ||
    config.method.toLocaleLowerCase() === "put"
  ) {
    // json
    if (config.headers?.['Content-Type'] === ContentType.json) {
      config.data = JSON.stringify(config.params);
    }
    // form
    if (config.headers?.['Content-Type'] === ContentType.form) {
      const formData = new FormData();
      if (_.isObject(config.params)) {
        _.forIn(config.params, (val, key) => {
          formData.append(key, val);
        });
      }
      config.data = formData;
    }
    config.params = null;
  }
  // console.log(config);
  return config;
}, error => {
  return Promise.reject(error);
});
// 响应拦截
axiosInstance.interceptors.response.use(response => {
  return response.data;
});

/**
 * request api
 * @param props: {
 *   options: { domain, url, params, method, headers },
 *   onSuccess: callback,
 *   onFail: callback,
 *   onFinal: callback,
 * }
 * @returns [fetching, run]
 */
export const useRequest = (props = {}) => {
  const history = useHistory();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async (runProps) => {
    const { onSuccess, onFail, onFinal, params, ...options } = props;
    setLoading(true);
    return axiosInstance({
      ...options,
      params: {
        ...params,
        ...runProps,
      },
      // ...axiosParams,
    })
    .then(res => {
      setLoading(false);
      // 回调
      if (typeof onFinal === "function") {
        onFinal(res, runProps);
      }
      switch (res?.code) {
        case "0":
          // 分页
          if (_.isObject(res?.data?.pagination) && res?.data?.pagination?.current > 1) {
            const pagination = res.data.pagination;
            const { current } = result?.pagination || {};
            const newResult = { ...(result || {}) };

            if (pagination?.current > current) {
              const newList = result.list || [];
              const currList = res?.data?.list || [];
              newList.push(...currList);
              newResult.pagination = pagination;
              newResult.list = newList;
              setResult(newResult);
            }
          } else {
            setResult(res?.data || null);
          }
          // 成功回调
          if (typeof onSuccess === "function") {
            onSuccess(res, runProps);
          }
          break;
        case "SESSION_TIMEOUT":
          // 退出登录
          logout("登录信息过期，请重新登录！", "warning");
          history.push("/");
          // 失败回调
          if (typeof onFail === "function") {
            onFail(res, runProps);
          }
          break;
        default:
          res?.message && toast({ msg: res.message, severity: "error" });
          // 失败回调
          if (typeof onFail === "function") {
            onFail(res, runProps);
          }
          break;
      }
      return res;
    })
    .catch(error => {
      let msg = error.toString();
      console.log(msg);
      if (msg?.indexOf("Error: timeout of") !== -1) {
        msg = "请求超时，请稍后再试";
      }
      msg && toast({ msg, severity: "error" });
      setLoading(false);
      // 回调
      if (typeof onFinal === "function") {
        onFinal({ code: -1, data: null, message: '', error }, runProps);
      }
    });
  };
  const runCallback = useCallback(run, [history, props, result]);
  return [runCallback, result, loading];
};
useRequest.propTypes = {
  onSuccess: PropTypes.func,
  onFail: PropTypes.func,
  onFinal: PropTypes.func,
  params: PropTypes.object,
  url: PropTypes.string.isRequired,
  headers: PropTypes.object,
}

export const useRequestAll = (options) => {
  const [loading, setLoading] = useState(false);
  const runAll = async (requests) => {
    setLoading(true);
    return axios.all(requests)
      .then(axios.spread((...args) => {
        setLoading(false);
        options?.then(...args);
      }))
      .catch(error => {
        setLoading(false);
        options?.catch(error);
      });
  }
  const runAllCallback = useCallback(runAll, [options]);
  return [runAllCallback, loading];
};
